import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type StaffRole } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from './AdminError';
import { StaffScreen } from './StaffScreen';

/**
 * Jobs, and what each one may do.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * One console for everybody. Whoever could see the refund queue could also
 * change the pricing model, edit the catalogue and create accounts — fine with
 * three people, unworkable with thirty, and it means a weekend support hire is
 * either handed the keys to the money or cannot do their job.
 *
 * ── WHY THE PERMISSIONS ARE A FIXED LIST ────────────────────────────────────
 *
 * Roles are yours to invent; permissions are not. Each one means something only
 * because a controller checks for it — so a free-text box would let somebody
 * create a role that silently grants nothing, and never find out. The catalogue
 * comes from the server, grouped, and a role can only ever hold what exists.
 *
 * ── AND WHY SOME BOXES CARRY A SENTENCE ─────────────────────────────────────
 *
 * Assigning a role is deciding what a colleague can do to the business. Four of
 * these can move money, approve a pharmacy to sell medicine to the public, or
 * grant the very permission being granted. A tick-box that does not say so is
 * a tick-box people tick.
 */
export function RolesScreen() {
  const [tab, setTab] = useState<'roles' | 'staff'>('roles');
  const [editing, setEditing] = useState<StaffRole | 'new' | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'staff-roles'],
    queryFn: ({ signal }) => api.staffRoles(signal),
  });

  if (isError) {
    return <AdminError title="We couldn't load the roles" error={error} onRetry={() => refetch()} />;
  }

  if (editing) {
    return (
      <RoleForm
        role={editing === 'new' ? null : editing}
        catalogue={data?.catalogue ?? {}}
        onDone={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[900px] px-4 py-6 xl:px-8">
      <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Access</h1>

      {/* Two halves of one decision: what the jobs are, and who is doing them.
          On separate screens you would invent a role and then go hunting for
          the person to give it to. */}
      <div className="mb-5 mt-3 flex gap-1 border-b border-line">
        {(
          [
            ['roles', 'Roles'],
            ['staff', 'Staff'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`access-tab-${key}`}
            className={cn(
              'min-h-11 border-b-2 bg-transparent px-3 text-[13.5px] font-semibold',
              tab === key ? 'border-blue text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'staff' ? (
        <StaffScreen />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <p className="m-0 max-w-[62ch] text-[13px] leading-relaxed text-muted">
              What each job may do. Make one up, tick what it needs, and put people in it — no
              deploy, and you can widen or narrow it any day.
            </p>

            <Button onClick={() => setEditing('new')}>New role</Button>
          </div>

          {isLoading ? (
            <div className="space-y-2.5">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="h-[86px] w-full" radius="card" />
              ))}
            </div>
          ) : (
            <div className="space-y-2.5">
              {(data?.roles ?? []).map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setEditing(role)}
                  data-testid={`role-${role.slug}`}
                  className="flex w-full min-h-11 items-start gap-3 rounded-card border border-line bg-white px-4 py-3 text-left hover:border-blue"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[14px] font-bold text-ink">{role.name}</span>
                      {role.is_system ? (
                        <span className="rounded-pill bg-paper px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-muted">
                          Ships with ChiCare
                        </span>
                      ) : null}
                    </span>
                    {role.description ? (
                      <span className="mt-0.5 block text-[12.5px] leading-snug text-muted">
                        {role.description}
                      </span>
                    ) : null}
                    <span className="mt-1 block font-mono text-[11px] text-faint">
                      {role.permission_count} permission{role.permission_count === 1 ? '' : 's'} ·{' '}
                      {role.people} {role.people === 1 ? 'person' : 'people'}
                    </span>
                  </span>

                  <Icon
                    name="chevron"
                    size={16}
                    strokeWidth={2}
                    className="mt-1 shrink-0 text-muted"
                  />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RoleForm({
  role,
  catalogue,
  onDone,
}: {
  role: StaffRole | null;
  catalogue: Record<string, Array<{ value: string; label: string; warning: string | null }>>;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissions, setPermissions] = useState<string[]>(role?.permissions ?? []);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'staff-roles'] });
    onDone();
  };

  const save = useMutation({
    mutationFn: () =>
      role
        ? api.updateStaffRole(role.id, { name, description, permissions })
        : api.createStaffRole({ name, description, permissions }),
    onSuccess: done,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteStaffRole(role!.id),
    onSuccess: done,
  });

  const error = [save.error, remove.error].find((e): e is ApiError => e instanceof ApiError);

  const toggle = (value: string) =>
    setPermissions((current) =>
      current.includes(value) ? current.filter((one) => one !== value) : [...current, value],
    );

  return (
    <div className="mx-auto w-full max-w-[760px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
      >
        <Icon name="back" size={15} strokeWidth={2} />
        All roles
      </button>

      <h1 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">
        {role ? role.name : 'A new role'}
      </h1>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="What is it called?"
          value={name}
          placeholder="Weekend support"
          onChange={(event) => setName(event.target.value)}
        />

        <Input
          label="What does this person do?"
          value={description}
          placeholder="Answers complaints at weekends. Cannot decide refunds."
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>

      <p className="m-0 mt-5 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        What they may do — {permissions.length} selected
      </p>

      <div className="mt-2 space-y-3">
        {Object.entries(catalogue).map(([group, items]) => (
          <section key={group} className="overflow-hidden rounded-card border border-line bg-white">
            <p className="m-0 border-b border-line px-4 py-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
              {group}
            </p>

            {items.map((item) => {
              const on = permissions.includes(item.value);

              return (
                <label
                  key={item.value}
                  className={cn(
                    'flex min-h-11 cursor-pointer items-start gap-2.5 border-b border-line-2 px-4 py-2.5 last:border-b-0',
                    on ? 'bg-blue-tint/40' : 'bg-white',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(item.value)}
                    data-testid={`perm-${item.value}`}
                    className="mt-0.5 h-4 w-4 shrink-0"
                  />

                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-ink">{item.label}</span>
                    {/* Said plainly where the consequence is not obvious. */}
                    {item.warning ? (
                      <span className="mt-0.5 flex items-start gap-1.5 text-[11.5px] leading-snug text-caution-ink">
                        <Icon name="alert" size={12} strokeWidth={2} className="mt-px shrink-0" />
                        {item.warning}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </section>
        ))}
      </div>

      {error ? (
        <p role="alert" className="m-0 mt-4 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 text-[12.5px] font-medium text-danger">
          {error.fieldErrors.name?.[0] ?? error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button disabled={save.isPending || name.trim() === ''} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : role ? 'Save this role' : 'Create the role'}
        </Button>

        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>

        {/* A role that ships with ChiCare can be re-scoped but not deleted —
            deleting the only one that can manage roles is a door that locks
            from the inside. */}
        {role && !role.is_system ? (
          <span className="ml-auto">
            {confirmingDelete ? (
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-[12.5px] text-muted">
                  {role.people > 0
                    ? `${role.people} still on it — move them first.`
                    : 'Delete this role?'}
                </span>
                <Button
                  size="sm"
                  variant="grey"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </Button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  className="min-h-11 bg-transparent px-1 text-[12.5px] font-semibold text-muted"
                >
                  Keep it
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="min-h-11 bg-transparent px-1 text-[12.5px] font-semibold text-muted"
              >
                Delete this role
              </button>
            )}
          </span>
        ) : null}
      </div>
    </div>
  );
}
